/**
 * useProfile – the app's single source of the signed-in identity.
 *
 * GET /users/me is the only thing that says WHO is signed in. This module
 * keeps one shared copy of that answer for every component (a module-level
 * store read through useSyncExternalStore), so the topbar, the pages and the
 * notification socket can never disagree about the current user.
 *
 * Status, and what each one means:
 *   idle        – nothing asked yet (or signed out)
 *   loading     – first request in flight; identity not known yet
 *   ok          – the server confirmed this profile
 *   offline     – no network, identity not confirmed yet
 *   unavailable – server unreachable / timed out / 5xx, identity not confirmed
 *   invalid     – the server said this session is over (logout follows)
 *
 * Failures are NOT collapsed into "no profile" any more: an unconfirmed
 * identity is shown as a neutral verifying/connection state (SessionGate),
 * never as a stored or invented name. Once the server has confirmed the
 * profile, a later outage keeps it (it was confirmed for this token) and
 * only the connection banner reports the outage.
 *
 * Call clearProfileCache() on logout so the next login triggers a fresh fetch.
 */
import { useEffect, useSyncExternalStore } from 'react';
import { apiFetch, performLogout } from '../context/AuthContext';
import { syncIdentityToStorage } from '../utils/authStorage';
import { sessionEndReasonFromBody, SESSION_END_REASONS } from '../utils/authErrors';
import {
  setRecoveryProbe,
  reportIdentityUnavailable,
  reportConnectionRestored,
  resetConnection,
} from '../utils/connectionStatus';

const BASE_URL = process.env.REACT_APP_BASE_URL;

// No answer within this window counts as "server unavailable".
export const PROFILE_TIMEOUT_MS = 15000;
// After this long the verifying screen says "Still connecting…".
export const PROFILE_SLOW_AFTER_MS = 8000;

const INITIAL_STATE = { profile: null, status: 'idle', failure: null, slow: false };

let state = INITIAL_STATE;
const listeners = new Set();
let inflight = null;
let slowTimer = null;

const setState = (patch) => {
  state = { ...state, ...patch };
  listeners.forEach((listener) => listener());
};

const subscribe = (listener) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

const getSnapshot = () => state;

const hasToken = () => {
  try {
    const token = localStorage.getItem('token');
    return Boolean(token) && token !== 'null' && token !== 'undefined';
  } catch (err) {
    return false;
  }
};

const clearSlowTimer = () => {
  if (slowTimer) {
    clearTimeout(slowTimer);
    slowTimer = null;
  }
};

// Ask the server who this is, and classify the answer. Never throws.
async function requestProfile() {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), PROFILE_TIMEOUT_MS);
  try {
    // reportConnection:false — this module reports the outcome itself, with
    // the reason, instead of apiFetch reporting a bare failure.
    const response = await apiFetch(`${BASE_URL}/users/me`, {
      method: 'GET',
      signal: controller.signal,
      reportConnection: false,
    });
    let body = null;
    try {
      body = await response.json();
    } catch (err) {
      body = null;
    }

    if (response.ok && body?.success && body?.profile) {
      return { kind: 'ok', profile: body.profile };
    }

    if (response.status === 401 || response.status === 403 || response.status === 404) {
      // Coded answers come from the auth middleware. The uncoded fallbacks
      // cover an older backend: on /users/me a 401 can only come from the
      // auth layer, and a 404 means the token's user has no profile.
      const reason =
        sessionEndReasonFromBody(response.status, body) ||
        (response.status === 401 || response.status === 404 ? SESSION_END_REASONS.ENDED : null) ||
        (response.status === 403 && /not active/i.test(String(body?.message || ''))
          ? SESSION_END_REASONS.INACTIVE
          : null);
      if (reason) return { kind: 'invalid', reason };
    }

    // 5xx / 503 AUTH_UNAVAILABLE / an expired token whose refresh could not
    // reach the server (498) / anything unexpected: the server could not
    // confirm the identity right now. Not a reason to end the session.
    return {
      kind: 'unavailable',
      referenceId: body?.referenceId || null,
      httpStatus: response.status,
    };
  } catch (err) {
    // fetch threw (offline, DNS, CORS, refused connection) or our timeout
    // aborted it.
    const offline = typeof navigator !== 'undefined' && navigator.onLine === false;
    return { kind: offline ? 'offline' : 'unavailable', referenceId: null };
  } finally {
    clearTimeout(timeoutId);
  }
}

/**
 * Fetch (or re-check) the identity.
 * @param {{ probe?: boolean }} [options] probe: called by the connection
 *   store's recovery loop — the outcome is returned to it instead of being
 *   reported back into it.
 */
export function loadProfile({ probe = false } = {}) {
  if (inflight) return inflight;
  if (!hasToken()) {
    return Promise.resolve({ kind: 'none' });
  }

  if (state.status === 'idle') {
    setState({ status: 'loading', failure: null, slow: false });
    clearSlowTimer();
    slowTimer = setTimeout(() => {
      if (state.status === 'loading') setState({ slow: true });
    }, PROFILE_SLOW_AFTER_MS);
  }

  inflight = requestProfile()
    .then((outcome) => {
      clearSlowTimer();
      // Signed out while the request was in flight — drop the answer.
      if (!hasToken()) return { kind: 'none' };

      if (outcome.kind === 'ok') {
        setState({ profile: outcome.profile, status: 'ok', failure: null, slow: false });
        syncIdentityToStorage(outcome.profile);
        if (!probe) reportConnectionRestored();
      } else if (outcome.kind === 'invalid') {
        setState({ profile: null, status: 'invalid', failure: { kind: 'invalid', reason: outcome.reason }, slow: false });
        performLogout(outcome.reason);
      } else if (state.profile) {
        // Already confirmed in this page — keep that identity; the outage is
        // the connection store's business (banner), not the identity's.
        if (!probe) reportIdentityUnavailable({ offline: outcome.kind === 'offline', referenceId: outcome.referenceId });
      } else {
        setState({
          status: outcome.kind,
          failure: { kind: outcome.kind, referenceId: outcome.referenceId || null, httpStatus: outcome.httpStatus || null },
          slow: false,
        });
        if (!probe) reportIdentityUnavailable({ offline: outcome.kind === 'offline', referenceId: outcome.referenceId });
      }
      return outcome;
    })
    .finally(() => {
      inflight = null;
    });

  return inflight;
}

// The connection store's recovery loop re-asks the server through here.
setRecoveryProbe(async () => {
  const outcome = await loadProfile({ probe: true });
  return {
    ok: outcome.kind === 'ok',
    invalid: outcome.kind === 'invalid',
    offline: outcome.kind === 'offline',
    referenceId: outcome.referenceId || null,
  };
});

/** Login already fetched /users/me — seed the store so nothing refetches. */
export function primeProfileCache(profile) {
  if (!profile) return;
  clearSlowTimer();
  setState({ profile, status: 'ok', failure: null, slow: false });
  reportConnectionRestored();
}

/** Read the current identity outside React. */
export function getProfileSnapshot() {
  return state;
}

export function useProfile() {
  const snapshot = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);

  useEffect(() => {
    if (getSnapshot().status === 'idle' && hasToken()) loadProfile();

    // Components that stay mounted across a whole SPA session (the
    // notification socket, the Mini Chat Dock) need to follow a login that
    // happens without a page reload. Logout is handled by clearProfileCache().
    const onLogin = () => {
      if (getSnapshot().status !== 'ok') loadProfile();
    };
    window.addEventListener('hrms:auth:login', onLogin);
    return () => window.removeEventListener('hrms:auth:login', onLogin);
  }, []);

  return {
    profile: snapshot.profile,
    // True until the identity is resolved one way or the other.
    profileLoading: snapshot.status === 'loading' || (snapshot.status === 'idle' && hasToken()),
    status: snapshot.status,
    failure: snapshot.failure,
    slow: snapshot.slow,
  };
}

/** Call this on logout to force a fresh fetch on the next login. */
export function clearProfileCache() {
  clearSlowTimer();
  inflight = null;
  state = INITIAL_STATE;
  listeners.forEach((listener) => listener());
  resetConnection();
}
